import { buildBatchContributionSummaryAggregation } from "./batch-contribution-summary";
import { buildBudgetVsActualAggregation } from "./budget-vs-actual";
import { buildGivingGapReportAggregation } from "./giving-gap-report";
import { buildMemberDirectoryAggregation } from "./member-directory";
import { buildMonthlyGivingSummaryAggregation } from "./monthly-giving-summary";
import { buildNewPeopleThisMonthAggregation } from "./new-people-this-month";
import { buildOpenRequestsByAgeAggregation } from "./open-requests-by-age";
import { buildProjectStatusOverviewAggregation } from "./project-status-overview";
import { buildWeeklyAttendanceReportAggregation } from "./weekly-attendance-report";
import { buildWhosMissingReportAggregation } from "./whos-missing";
import { buildYearEndGivingStatementsAggregation } from "./year-end-giving-statements";
import { buildStatementOfActivityAggregation } from "./statement-of-activity";
import { buildBalanceSheetAggregation } from "./balance-sheet";
import { buildTrialBalanceAggregation } from "./trial-balance";
import { buildGeneralLedgerDetailAggregation } from "./general-ledger-detail";
import { buildCashFlowStatementAggregation } from "./cash-flow-statement";
import { buildARAggingAggregation } from "./ar-aging";
import { buildAPAgingAggregation } from "./ap-aging";
import { buildFundBalanceSummaryAggregation } from "./fund-balance-summary";
import { buildBankReconciliationReportAggregation } from "./bank-reconciliation-report";
import { buildContributionDetailAggregation } from "./contribution-detail";
import { buildVolunteerScheduleReportAggregation } from "./volunteer-schedule-report";
import { buildGroupRosterReportAggregation } from "./group-roster-report";
import { buildGivingByFundAggregation } from "./giving-by-fund";
import { buildFinancialVarianceAggregation } from "./financial-variance";
import { buildFundHealthAggregation } from "./fund-health";
import { buildEngagementHealthAggregation } from "./engagement-health";
import { buildCareLoadAggregation } from "./care-load";
import { buildFunctionalExpensesAggregation } from "./functional-expenses";
import { buildLiquidityDisclosureAggregation } from "./liquidity-disclosure";
import { buildEndowmentPerformanceAggregation } from "./endowment-performance";
import { buildInKindContributionsAggregation } from "./inkind-contributions";
import { buildPledgeReceivableAgingAggregation } from "./pledge-receivable-aging";
import { buildMinistryGrowthAggregation } from "./ministry-growth";
import { buildMinistryParticipationAggregation } from "./ministry-participation";
import { buildMinistryVolunteerUtilizationAggregation } from "./ministry-volunteer-utilization";
import { buildSacramentalRecordsAggregation } from "./sacramental-records";
import { type ReportAggregation, type ReportRow, type ReportSummaryItem } from "@glorify-llc/accounting-core";
import { type BatchContributionSummaryContext } from "./batch-contribution-summary";
import { type BudgetVsActualContext } from "./budget-vs-actual";
import { type GivingGapReportContext } from "./giving-gap-report";
import { type MemberDirectoryContext } from "./member-directory";
import { type MonthlyGivingSummaryContext } from "./monthly-giving-summary";
import { type NewPeopleThisMonthContext } from "./new-people-this-month";
import { type OpenRequestsByAgeContext } from "./open-requests-by-age";
import { type ProjectStatusOverviewContext } from "./project-status-overview";
import { type WeeklyAttendanceReportContext } from "./weekly-attendance-report";
import { type WhosMissingReportContext } from "./whos-missing";
import { type YearEndGivingStatementsContext } from "./year-end-giving-statements";
import { type StatementOfActivityContext } from "./statement-of-activity";
import { type BalanceSheetContext } from "./balance-sheet";
import { type TrialBalanceContext } from "./trial-balance";
import { type GeneralLedgerDetailContext } from "./general-ledger-detail";
import { type CashFlowStatementContext } from "./cash-flow-statement";
import { type ARAgingContext } from "./ar-aging";
import { type APAgingContext } from "./ap-aging";
import { type FundBalanceSummaryContext } from "./fund-balance-summary";
import { type BankReconciliationReportContext } from "./bank-reconciliation-report";
import { type ContributionDetailContext } from "./contribution-detail";
import { type VolunteerScheduleReportContext } from "./volunteer-schedule-report";
import { type GroupRosterReportContext } from "./group-roster-report";
import { type GivingByFundContext } from "./giving-by-fund";
import { type FinancialVarianceContext } from "./financial-variance";
import { type FundHealthContext } from "./fund-health";
import { type EngagementHealthContext } from "./engagement-health";
import { type CareLoadContext } from "./care-load";
import { type FunctionalExpensesContext } from "./functional-expenses";
import { type LiquidityDisclosureContext } from "./liquidity-disclosure";
import { type EndowmentPerformanceContext } from "./endowment-performance";
import { type InKindContributionsContext } from "./inkind-contributions";
import { type PledgeReceivableAgingContext } from "./pledge-receivable-aging";
import { type MinistryGrowthContext } from "./ministry-growth";
import { type MinistryParticipationContext } from "./ministry-participation";
import { type MinistryVolunteerUtilizationContext } from "./ministry-volunteer-utilization";
import { type SacramentalRecordsContext } from "./sacramental-records";

export type { ReportAggregation, ReportRow, ReportSummaryItem };

export type ReportAdapterContextMap = {
  "batch-contribution-summary": BatchContributionSummaryContext;
  "budget-vs-actual": BudgetVsActualContext;
  "giving-gap-report": GivingGapReportContext;
  "member-directory": MemberDirectoryContext;
  "monthly-giving-summary": MonthlyGivingSummaryContext;
  "new-people-this-month": NewPeopleThisMonthContext;
  "open-requests-by-age": OpenRequestsByAgeContext;
  "project-status-overview": ProjectStatusOverviewContext;
  "weekly-attendance-report": WeeklyAttendanceReportContext;
  "whos-missing": WhosMissingReportContext;
  "year-end-giving-statements": YearEndGivingStatementsContext;
  "statement-of-activity": StatementOfActivityContext;
  "balance-sheet": BalanceSheetContext;
  "trial-balance": TrialBalanceContext;
  "general-ledger-detail": GeneralLedgerDetailContext;
  "cash-flow-statement": CashFlowStatementContext;
  "ar-aging": ARAgingContext;
  "ap-aging": APAgingContext;
  "fund-balance-summary": FundBalanceSummaryContext;
  "bank-reconciliation-report": BankReconciliationReportContext;
  "contribution-detail": ContributionDetailContext;
  "volunteer-schedule-report": VolunteerScheduleReportContext;
  "group-roster-report": GroupRosterReportContext;
  "giving-by-fund": GivingByFundContext;
  "financial-variance": FinancialVarianceContext;
  "fund-health": FundHealthContext;
  "engagement-health": EngagementHealthContext;
  "care-load": CareLoadContext;
  "functional-expenses": FunctionalExpensesContext;
  "liquidity-disclosure": LiquidityDisclosureContext;
  "endowment-performance": EndowmentPerformanceContext;
  "inkind-contributions": InKindContributionsContext;
  "pledge-receivable-aging": PledgeReceivableAgingContext;
  "ministry-growth": MinistryGrowthContext;
  "ministry-participation": MinistryParticipationContext;
  "ministry-volunteer-utilization": MinistryVolunteerUtilizationContext;
  "sacramental-records": SacramentalRecordsContext;
};

export type ReportAdapterKey = keyof ReportAdapterContextMap;

export type ReportAdapter<K extends ReportAdapterKey = ReportAdapterKey> = (
  context: ReportAdapterContextMap[K]
) => ReportAggregation;

export const reportAdapters: { [K in ReportAdapterKey]: ReportAdapter<K> } = {
  "batch-contribution-summary": buildBatchContributionSummaryAggregation,
  "budget-vs-actual": buildBudgetVsActualAggregation,
  "giving-gap-report": buildGivingGapReportAggregation,
  "member-directory": buildMemberDirectoryAggregation,
  "monthly-giving-summary": buildMonthlyGivingSummaryAggregation,
  "new-people-this-month": buildNewPeopleThisMonthAggregation,
  "open-requests-by-age": buildOpenRequestsByAgeAggregation,
  "project-status-overview": buildProjectStatusOverviewAggregation,
  "weekly-attendance-report": buildWeeklyAttendanceReportAggregation,
  "whos-missing": buildWhosMissingReportAggregation,
  "year-end-giving-statements": buildYearEndGivingStatementsAggregation,
  "statement-of-activity": buildStatementOfActivityAggregation,
  "balance-sheet": buildBalanceSheetAggregation,
  "trial-balance": buildTrialBalanceAggregation,
  "general-ledger-detail": buildGeneralLedgerDetailAggregation,
  "cash-flow-statement": buildCashFlowStatementAggregation,
  "ar-aging": buildARAggingAggregation,
  "ap-aging": buildAPAgingAggregation,
  "fund-balance-summary": buildFundBalanceSummaryAggregation,
  "bank-reconciliation-report": buildBankReconciliationReportAggregation,
  "contribution-detail": buildContributionDetailAggregation,
  "volunteer-schedule-report": buildVolunteerScheduleReportAggregation,
  "group-roster-report": buildGroupRosterReportAggregation,
  "giving-by-fund": buildGivingByFundAggregation,
  "financial-variance": buildFinancialVarianceAggregation,
  "fund-health": buildFundHealthAggregation,
  "engagement-health": buildEngagementHealthAggregation,
  "care-load": buildCareLoadAggregation,
  "functional-expenses": buildFunctionalExpensesAggregation,
  "liquidity-disclosure": buildLiquidityDisclosureAggregation,
  "endowment-performance": buildEndowmentPerformanceAggregation,
  "inkind-contributions": buildInKindContributionsAggregation,
  "pledge-receivable-aging": buildPledgeReceivableAgingAggregation,
  "ministry-growth": buildMinistryGrowthAggregation,
  "ministry-participation": buildMinistryParticipationAggregation,
  "ministry-volunteer-utilization": buildMinistryVolunteerUtilizationAggregation,
  "sacramental-records": buildSacramentalRecordsAggregation,
};

export {
  buildBatchContributionSummaryAggregation,
  buildBudgetVsActualAggregation,
  buildGivingGapReportAggregation,
  buildMemberDirectoryAggregation,
  buildMonthlyGivingSummaryAggregation,
  buildNewPeopleThisMonthAggregation,
  buildOpenRequestsByAgeAggregation,
  buildProjectStatusOverviewAggregation,
  buildWeeklyAttendanceReportAggregation,
  buildWhosMissingReportAggregation,
  buildYearEndGivingStatementsAggregation,
  buildStatementOfActivityAggregation,
  buildBalanceSheetAggregation,
  buildTrialBalanceAggregation,
  buildGeneralLedgerDetailAggregation,
  buildCashFlowStatementAggregation,
  buildARAggingAggregation,
  buildAPAgingAggregation,
  buildFundBalanceSummaryAggregation,
  buildBankReconciliationReportAggregation,
  buildContributionDetailAggregation,
  buildVolunteerScheduleReportAggregation,
  buildGroupRosterReportAggregation,
  buildGivingByFundAggregation,
  buildFinancialVarianceAggregation,
  buildFundHealthAggregation,
  buildEngagementHealthAggregation,
  buildCareLoadAggregation,
  buildFunctionalExpensesAggregation,
  buildLiquidityDisclosureAggregation,
  buildEndowmentPerformanceAggregation,
  buildInKindContributionsAggregation,
  buildPledgeReceivableAgingAggregation,
  buildMinistryGrowthAggregation,
  buildMinistryParticipationAggregation,
  buildMinistryVolunteerUtilizationAggregation,
  buildSacramentalRecordsAggregation,
};
